import { Component, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangleIcon } from '@/shared/components/icons'
import { Button, IconTile } from '@/shared/components/ui'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-canvas px-4 text-center">
          <IconTile tone="danger">
            <AlertTriangleIcon className="h-6 w-6" aria-hidden="true" />
          </IconTile>
          <h1 className="mt-4 text-xl font-bold text-navy-900">Something went wrong</h1>
          <p className="mt-2 max-w-sm text-sm text-neutral-600">
            An unexpected error occurred. Your data is safe. Please refresh the page and try again.
          </p>

          {import.meta.env.DEV && this.state.error && (
            <pre className="scroll-slim mt-5 max-h-60 max-w-xl overflow-auto rounded-xl border border-danger-200 bg-danger-50 p-4 text-left text-xs text-danger-700">
              {this.state.error.message}
              {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
            </pre>
          )}

          <div className="mt-6">
            <Button variant="primary" size="md" onClick={() => window.location.reload()}>
              Refresh page
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
